import { useEffect, useState, useCallback, useRef } from "react";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from "@dnd-kit/core";
import { Folder, FolderPlus, FolderOpen, Upload, File as FileIcon, Trash2, Download, Plus, ChevronRight, ChevronDown, Loader2, GripVertical, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

function DraggableRow({ id, data, children, className }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      className={`${className} ${isDragging ? "opacity-40" : ""}`}
      style={{ touchAction: "none" }}>
      {children}
    </div>
  );
}

function DropZone({ id, data, children, className, activeClass = "" }) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return <div ref={setNodeRef} className={`${className} ${isOver ? activeClass : ""}`}>{children}</div>;
}

export default function WorkspaceFiles({ apiBase, q = "" }) {
  const [tree, setTree] = useState({ sections: [], folders: [], files: [] });
  const [activeSection, setActiveSection] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingTo, setUploadingTo] = useState(null);
  const [dragItem, setDragItem] = useState(null);
  const fileInputRef = useRef(null);
  const pendingFolder = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const url = (p) => `${apiBase}${p}${q}`;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(url("/tree"));
      setTree(data);
      setActiveSection((prev) => prev && data.sections.some((s) => s.id === prev) ? prev : (data.sections[0]?.id || null));
    } catch { toast.error("Couldn't load your files."); }
    finally { setLoading(false); }
  }, [apiBase, q]);
  useEffect(() => { load(); }, [load]);

  const addSection = async () => {
    const name = window.prompt("Name your new tab (e.g. Contracts, 2026 Taxes):");
    if (!name) return;
    try { const { data } = await api.post(url("/sections"), { name }); await load(); setActiveSection(data.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add tab."); }
  };
  const deleteSection = async (sid) => {
    if (!window.confirm("Delete this tab? Its folders move to your first tab.")) return;
    try { await api.delete(url(`/sections/${sid}`)); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete tab."); }
  };
  const addFolder = async (parentId) => {
    const name = window.prompt("Folder name:");
    if (!name) return;
    try { await api.post(url("/folders"), { name, section: activeSection, parent_id: parentId || null }); await load(); if (parentId) setExpanded((e) => ({ ...e, [parentId]: true })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create folder."); }
  };
  const renameFolder = async (f) => {
    const name = window.prompt("Rename folder:", f.name);
    if (!name || name === f.name) return;
    try { await api.patch(url(`/folders/${f.id}`), { name }); await load(); }
    catch { toast.error("Couldn't rename."); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete "${f.name}" and everything inside it?`)) return;
    try { await api.delete(url(`/folders/${f.id}`)); await load(); }
    catch { toast.error("Couldn't delete folder."); }
  };
  const triggerUpload = (folderId) => { pendingFolder.current = folderId || null; fileInputRef.current?.click(); };
  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingTo(pendingFolder.current || "root");
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      if (pendingFolder.current) form.append("folder_id", pendingFolder.current);
      try { await api.post(url("/files"), form, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`); }
    }
    setUploadingTo(null);
    toast.success(`${files.length} file${files.length === 1 ? "" : "s"} uploaded.`);
    await load();
  };
  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"?`)) return;
    try { await api.delete(url(`/files/${f.id}`)); await load(); }
    catch { toast.error("Couldn't delete file."); }
  };
  const downloadFile = async (f) => {
    try {
      const res = await api.get(url(`/files/${f.id}/download`), { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = f.filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
    } catch { toast.error("Couldn't download file."); }
  };

  const onDragEnd = async ({ active, over }) => {
    setDragItem(null);
    if (!over) return;
    const a = active.data.current, o = over.data.current;
    if (!a || !o) return;
    try {
      if (a.type === "folder") {
        if (o.type === "section") await api.patch(url(`/folders/${a.id}`), { section: o.section, parent_id: null });
        else if (o.type === "folder" && o.id !== a.id) await api.patch(url(`/folders/${a.id}`), { parent_id: o.id, section: o.section });
        else if (o.type === "root") await api.patch(url(`/folders/${a.id}`), { parent_id: null, section: activeSection });
        else return;
      } else if (a.type === "file") {
        if (o.type === "folder") await api.patch(url(`/files/${a.id}`), { folder_id: o.id });
        else if (o.type === "root") await api.patch(url(`/files/${a.id}`), { folder_id: null });
        else return;
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Move failed."); }
  };

  if (loading) return <div className="py-16 text-center text-[#1e3d3a]/50"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const childFolders = (pid) => tree.folders.filter((f) => f.section === activeSection && (f.parent_id || null) === (pid || null));
  const filesIn = (fid) => tree.files.filter((f) => (f.folder_id || null) === (fid || null));
  const rootFiles = tree.files.filter((f) => !f.folder_id);

  const renderFolder = (f, depth) => {
    const kids = childFolders(f.id);
    const fFiles = filesIn(f.id);
    const isOpen = expanded[f.id];
    return (
      <div key={f.id} style={{ marginLeft: depth * 14 }}>
        <DropZone id={`folder-${f.id}`} data={{ type: "folder", id: f.id, section: f.section }}
          className="rounded-lg" activeClass="ring-2 ring-[#c9a961] bg-[#c9a961]/10">
          <DraggableRow id={`folder-${f.id}`} data={{ type: "folder", id: f.id, section: f.section }}
            className="group flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-[#1e3d3a]/[0.04] cursor-grab">
            <button onClick={() => setExpanded((e) => ({ ...e, [f.id]: !e[f.id] }))} className="text-[#1e3d3a]/40 shrink-0" data-testid={`ws-folder-toggle-${f.id}`}>
              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            {isOpen ? <FolderOpen className="h-4 w-4 text-[#c9a961] shrink-0" /> : <Folder className="h-4 w-4 text-[#c9a961] shrink-0" />}
            <span className="text-sm text-[#1e3d3a] truncate flex-1" data-testid={`ws-folder-${f.id}`}>{f.name}</span>
            <span className="hidden group-hover:flex items-center gap-1 shrink-0">
              <button title="New subfolder" onClick={() => addFolder(f.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#a3823f]"><FolderPlus className="h-3.5 w-3.5" /></button>
              <button title="Upload here" onClick={() => triggerUpload(f.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#a3823f]" data-testid={`ws-upload-${f.id}`}><Upload className="h-3.5 w-3.5" /></button>
              <button title="Rename" onClick={() => renameFolder(f)} className="p-1 text-[#1e3d3a]/40 hover:text-[#a3823f]">✎</button>
              <button title="Delete" onClick={() => deleteFolder(f)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
            </span>
            {uploadingTo === f.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#c9a961]" />}
          </DraggableRow>
        </DropZone>
        {isOpen && (
          <div>
            {kids.map((c) => renderFolder(c, depth + 1))}
            {fFiles.map((file) => renderFile(file, depth + 1))}
            {kids.length === 0 && fFiles.length === 0 && (
              <div style={{ marginLeft: (depth + 1) * 14 }} className="px-2 py-1 text-[11px] text-[#1e3d3a]/35">Empty — drop files or folders here</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderFile = (file, depth) => (
    <div key={file.id} style={{ marginLeft: depth * 14 }}>
      <DraggableRow id={`file-${file.id}`} data={{ type: "file", id: file.id }}
        className="group flex items-center gap-1.5 rounded-lg px-2 py-1.5 hover:bg-[#1e3d3a]/[0.04] cursor-grab">
        <span className="w-3.5 shrink-0" />
        <FileIcon className="h-4 w-4 text-[#1e3d3a]/45 shrink-0" />
        <span className="text-sm text-[#1e3d3a]/85 truncate flex-1" data-testid={`ws-file-${file.id}`}>{file.filename}</span>
        <span className="text-[10px] text-[#1e3d3a]/40 shrink-0">{fmtSize(file.size)}</span>
        <span className="hidden group-hover:flex items-center gap-1 shrink-0">
          <button title="Download" onClick={() => downloadFile(file)} className="p-1 text-[#1e3d3a]/40 hover:text-[#a3823f]"><Download className="h-3.5 w-3.5" /></button>
          <button title="Delete" onClick={() => deleteFile(file)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
        </span>
      </DraggableRow>
    </div>
  );

  const roots = childFolders(null);

  return (
    <div data-testid="workspace-files">
      <input ref={fileInputRef} type="file" multiple hidden onChange={onFilePicked} data-testid="ws-file-input" />
      <DndContext sensors={sensors} onDragStart={({ active }) => setDragItem(active.data.current)} onDragEnd={onDragEnd} onDragCancel={() => setDragItem(null)}>
        {/* Section tabs (drop targets) */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {tree.sections.map((s) => (
            <DropZone key={s.id} id={`section-${s.id}`} data={{ type: "section", section: s.id }}
              className="rounded-full" activeClass="ring-2 ring-[#c9a961]">
              <button
                data-testid={`ws-section-${s.id}`}
                onClick={() => setActiveSection(s.id)}
                className={`group inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${activeSection === s.id ? "bg-[#1e3d3a] text-white" : "bg-[#1e3d3a]/6 text-[#1e3d3a]/70 hover:bg-[#1e3d3a]/10"}`}>
                {s.name}
                {tree.sections.length > 1 && (
                  <span onClick={(e) => { e.stopPropagation(); deleteSection(s.id); }} className="opacity-0 group-hover:opacity-60 hover:!opacity-100"><X className="h-3 w-3" /></span>
                )}
              </button>
            </DropZone>
          ))}
          <button onClick={addSection} data-testid="ws-add-section" className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#1e3d3a]/25 px-3 py-1.5 text-sm text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f]"><Plus className="h-3.5 w-3.5" /> Tab</button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => addFolder(null)} data-testid="ws-new-folder" className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961]/15 px-3 py-1.5 text-[13px] font-medium text-[#8a6d2f] hover:bg-[#c9a961]/25"><FolderPlus className="h-4 w-4" /> New folder</button>
          <button onClick={() => triggerUpload(null)} data-testid="ws-upload-root" className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a]/6 px-3 py-1.5 text-[13px] font-medium text-[#1e3d3a]/75 hover:bg-[#1e3d3a]/10"><Upload className="h-4 w-4" /> Upload{uploadingTo === "root" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}</button>
          <span className="text-[11px] text-[#1e3d3a]/40 ml-auto">Drag folders between tabs · drag files into folders</span>
        </div>

        {/* Root drop zone + tree */}
        <DropZone id="root-zone" data={{ type: "root" }}
          className="min-h-[200px] rounded-xl border border-[#1e3d3a]/10 bg-white p-2" activeClass="ring-2 ring-[#c9a961] bg-[#c9a961]/5">
          {roots.length === 0 && rootFiles.length === 0 ? (
            <div className="py-14 text-center text-sm text-[#1e3d3a]/40">
              No folders yet in this tab. Create a folder or upload files to get started.
            </div>
          ) : (
            <>
              {roots.map((f) => renderFolder(f, 0))}
              {rootFiles.length > 0 && (
                <div className="mt-2 border-t border-[#1e3d3a]/8 pt-2">
                  <div className="px-2 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/40 mb-1">Unfiled</div>
                  {rootFiles.map((file) => renderFile(file, 0))}
                </div>
              )}
            </>
          )}
        </DropZone>

        <DragOverlay>
          {dragItem && (
            <div className="rounded-lg bg-white shadow-lg border border-[#c9a961]/50 px-3 py-1.5 text-sm text-[#1e3d3a] flex items-center gap-1.5">
              {dragItem.type === "folder" ? <Folder className="h-4 w-4 text-[#c9a961]" /> : <FileIcon className="h-4 w-4 text-[#1e3d3a]/45" />}
              Moving…
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
